import { EMPTY_INPUT } from "@marshmallow-duel/simulation";

import type { PlayerInput, PlayerSlot } from "@marshmallow-duel/simulation";

type Action = keyof PlayerInput;

const keyboardBindings: Readonly<Record<string, readonly [PlayerSlot, Action][]>> = {
  KeyA: [[1, "left"]],
  KeyD: [[1, "right"]],
  KeyW: [[1, "up"]],
  KeyS: [[1, "down"]],
  KeyQ: [[1, "shield"]],
  Digit1: [[1, "rope"]],
  KeyF: [[1, "ability"]],
  ArrowLeft: [[2, "left"]],
  ArrowRight: [[2, "right"]],
  ArrowUp: [[2, "up"]],
  ArrowDown: [[2, "down"]],
  ShiftLeft: [[2, "shield"]],
  ShiftRight: [[2, "shield"]],
  Enter: [[2, "rope"]],
  ControlLeft: [[2, "ability"]],
  ControlRight: [[2, "ability"]],
  KeyJ: [[3, "left"]],
  KeyL: [[3, "right"]],
  KeyI: [[3, "up"]],
  KeyK: [[3, "down"]],
  KeyP: [[3, "shield"]],
  KeyO: [[3, "rope"]],
  KeyU: [[3, "ability"]],
  Numpad4: [[4, "left"]],
  Numpad6: [[4, "right"]],
  Numpad8: [[4, "up"]],
  Numpad5: [[4, "down"]],
  Numpad0: [[4, "shield"]],
  Numpad9: [[4, "rope"]],
  Numpad7: [[4, "ability"]]
};

const gamepadButton = (gamepad: Gamepad, index: number): boolean => gamepad.buttons[index]?.pressed ?? false;
const gamepadInput = (gamepad: Gamepad | null): PlayerInput => {
  if (!gamepad) return EMPTY_INPUT;
  return {
    left: (gamepad.axes[0] ?? 0) < -0.35 || gamepadButton(gamepad, 14),
    right: (gamepad.axes[0] ?? 0) > 0.35 || gamepadButton(gamepad, 15),
    up: (gamepad.axes[1] ?? 0) < -0.35 || gamepadButton(gamepad, 12) || gamepadButton(gamepad, 0),
    down: (gamepad.axes[1] ?? 0) > 0.35 || gamepadButton(gamepad, 13),
    shield: gamepadButton(gamepad, 3),
    rope: gamepadButton(gamepad, 2),
    ability: gamepadButton(gamepad, 1)
  };
};

export class InputManager {
  readonly #gamepadsEnabled: boolean;
  readonly #keys = new Set<string>();
  readonly #onKeyDown = (event: KeyboardEvent) => {
    if (keyboardBindings[event.code]) event.preventDefault();
    this.#keys.add(event.code);
  };
  readonly #onKeyUp = (event: KeyboardEvent) => {
    if (keyboardBindings[event.code]) event.preventDefault();
    this.#keys.delete(event.code);
  };
  readonly #onBlur = () => this.#keys.clear();

  constructor(gamepadsEnabled = true) {
    this.#gamepadsEnabled = gamepadsEnabled;
  }

  attach(): void {
    window.addEventListener("keydown", this.#onKeyDown, { passive: false });
    window.addEventListener("keyup", this.#onKeyUp, { passive: false });
    window.addEventListener("blur", this.#onBlur);
  }

  detach(): void {
    window.removeEventListener("keydown", this.#onKeyDown);
    window.removeEventListener("keyup", this.#onKeyUp);
    window.removeEventListener("blur", this.#onBlur);
    this.#keys.clear();
  }

  read(playerCount: number): Partial<Record<PlayerSlot, PlayerInput>> {
    const mutable = new Map<PlayerSlot, Record<Action, boolean>>();
    for (let slot = 1; slot <= playerCount; slot += 1) mutable.set(slot as PlayerSlot, { ...EMPTY_INPUT });
    for (const code of this.#keys) {
      for (const [slot, action] of keyboardBindings[code] ?? []) {
        const input = mutable.get(slot);
        if (input) input[action] = true;
      }
    }
    const gamepads = this.#gamepadsEnabled ? navigator.getGamepads?.() ?? [] : [];
    for (let slot = 1; slot <= playerCount; slot += 1) {
      const current = mutable.get(slot as PlayerSlot);
      if (!current) continue;
      const pad = gamepadInput(gamepads[slot - 1] ?? null);
      for (const action of Object.keys(EMPTY_INPUT) as Action[]) current[action] ||= pad[action];
    }
    return Object.fromEntries(mutable) as Partial<Record<PlayerSlot, PlayerInput>>;
  }
}
